import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ArrowLeft,
  RotateCcw,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

type AgentRunStatus =
  | "pending"
  | "running"
  | "succeeded"
  | "failed"
  | "needs_human";

type AgentRunDetail = {
  id: string;
  asin: string;
  customerEmail: string;
  status: AgentRunStatus;
  caseId: string | null;
  errorCategory: string | null;
  errorMessage: string | null;
  screenshotsKey: string | null;
  costUsd: string | null;
  startedAt: string;
  completedAt: string | null;
  duration: number | null;
  violations: unknown[];
  takedownRequest: string | null;
  browserbaseSessionId: string | null;
  browserbaseLiveUrl: string | null;
  humanReason: string | null;
  humanRequestedAt: string | null;
  humanExpiresAt: string | null;
  lastCheckpoint: string | null;
};

type Screenshot = {
  key: string;
  url: string;
  step: number;
};

const STATUS_VARIANT: Record<
  AgentRunStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  running: "default",
  succeeded: "outline",
  failed: "destructive",
  needs_human: "outline",
};

const STATUS_LABEL: Record<AgentRunStatus, string> = {
  pending: "Pending",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  needs_human: "Needs human",
};

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function formatTs(iso: string): string {
  return iso.slice(0, 19).replace("T", " ");
}

function minutesUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.round(ms / 60000);
}

function AdminAgentRunDetailInner({ runId }: { runId: string }) {
  const [run, setRun] = useState<AgentRunDetail | null>(null);
  const [loadingRun, setLoadingRun] = useState(true);
  const [runError, setRunError] = useState<string | null>(null);

  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [overrideCaseId, setOverrideCaseId] = useState("");

  const reloadRun = useCallback(() => {
    setLoadingRun(true);
    setActionSuccess(null);
    setActionError(null);
    api<AgentRunDetail>(`/admin/agent-runs/${runId}`).then((res) => {
      setLoadingRun(false);
      if (res.ok && res.data) {
        setRun(res.data);
      } else {
        setRunError(res.error ?? "Failed to load run");
      }
    });
  }, [runId]);

  const handleRerun = useCallback(async () => {
    setActionLoading(true);
    setActionError(null);
    const res = await api(`/admin/agent-runs/${runId}/rerun`, { method: "POST" });
    setActionLoading(false);
    if (res.ok) {
      setActionSuccess("Run has been reset to pending.");
      setTimeout(reloadRun, 800);
    } else {
      setActionError(res.error ?? "Failed to re-run");
    }
  }, [runId, reloadRun]);

  const handleManualOverride = useCallback(async (action: "submitted" | "rejected") => {
    setActionLoading(true);
    setActionError(null);
    const res = await api(`/admin/agent-runs/${runId}/manual-override`, {
      method: "POST",
      body: JSON.stringify({ caseId: overrideCaseId || undefined, action }),
    });
    setActionLoading(false);
    if (res.ok) {
      setActionSuccess(`Marked as ${action}.`);
      setTimeout(reloadRun, 800);
    } else {
      setActionError(res.error ?? "Failed to apply override");
    }
  }, [runId, overrideCaseId, reloadRun]);

  const handleOpenLiveBrowser = useCallback(async () => {
    setActionError(null);
    const res = await api<{ liveUrl: string; expiresAt: string }>(
      `/admin/agent-runs/${runId}/live-session`,
    );
    if (res.ok && res.data?.liveUrl) {
      window.open(res.data.liveUrl, "_blank", "noopener,noreferrer");
    } else if (res.status === 404) {
      setActionError("Live session expired or unavailable.");
      setTimeout(reloadRun, 400);
    } else {
      setActionError(res.error ?? "Failed to open live session");
    }
  }, [runId, reloadRun]);

  const handleContinueHuman = useCallback(async () => {
    setActionLoading(true);
    setActionError(null);
    const res = await api(`/admin/agent-runs/${runId}/continue`, { method: "POST" });
    setActionLoading(false);
    if (res.ok) {
      setActionSuccess("Resuming agent — run flipped back to pending.");
      setTimeout(reloadRun, 600);
    } else {
      setActionError(res.error ?? "Failed to resume run");
    }
  }, [runId, reloadRun]);

  const handleAbortHuman = useCallback(async () => {
    setActionLoading(true);
    setActionError(null);
    const abortRes = await api(`/admin/agent-runs/${runId}/abort-human`, { method: "POST" });
    if (!abortRes.ok) {
      setActionLoading(false);
      setActionError(abortRes.error ?? "Failed to abort run");
      return;
    }
    const rerunRes = await api(`/admin/agent-runs/${runId}/rerun`, { method: "POST" });
    setActionLoading(false);
    if (rerunRes.ok) {
      setActionSuccess("Aborted handoff and queued a fresh re-run.");
      setTimeout(reloadRun, 600);
    } else {
      setActionError(rerunRes.error ?? "Aborted, but failed to queue re-run");
    }
  }, [runId, reloadRun]);

  useEffect(() => {
    setLoadingRun(true);
    api<AgentRunDetail>(`/admin/agent-runs/${runId}`).then((res) => {
      setLoadingRun(false);
      if (res.ok && res.data) {
        setRun(res.data);
      } else {
        setRunError(res.error ?? "Failed to load run");
      }
    });
  }, [runId]);

  useEffect(() => {
    if (!run) return;
    setLoadingScreenshots(true);
    api<{ screenshots: Screenshot[] }>(`/admin/agent-runs/${runId}/screenshots`).then(
      (res) => {
        setLoadingScreenshots(false);
        if (res.ok && res.data) {
          setScreenshots(res.data.screenshots);
          setCurrentStep(0);
        }
      },
    );
  }, [run, runId]);

  if (loadingRun) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (runError || !run) {
    return (
      <div className="p-6">
        <p className="text-destructive">{runError ?? "Run not found"}</p>
        <Link to="/admin/agent-runs" className="mt-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to agent runs
        </Link>
      </div>
    );
  }

  const hasError = run.errorCategory || run.errorMessage;
  const isAwaitingHuman = run.status === "needs_human";
  const expiresInMinutes = minutesUntil(run.humanExpiresAt);
  const handoffExpired = expiresInMinutes !== null && expiresInMinutes <= 0;
  const latestScreenshot =
    screenshots.length > 0 ? screenshots[screenshots.length - 1] : null;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/admin/agent-runs"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Back to agent runs
      </Link>

      {/* Human-action-required card — top of detail when status=needs_human */}
      {isAwaitingHuman && (
        <Card className="border-amber-300 bg-amber-50 ring-1 ring-amber-200 dark:border-amber-900 dark:bg-amber-950/40 dark:ring-amber-900">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-5 w-5" />
                  Human action required
                </CardTitle>
                <CardDescription className="text-amber-700 dark:text-amber-400">
                  {run.humanReason ?? "Agent is paused — operator input needed."}
                </CardDescription>
              </div>
              <div className="text-right text-xs tabular-nums text-amber-700 dark:text-amber-400">
                {handoffExpired ? (
                  <span className="font-medium">Handoff expired</span>
                ) : expiresInMinutes !== null ? (
                  <>Expires in {expiresInMinutes} min</>
                ) : null}
                {run.humanExpiresAt && (
                  <div className="text-amber-600/80 dark:text-amber-500/80">{formatTs(run.humanExpiresAt)}</div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {actionSuccess && (
              <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                <CheckCircle2 className="h-4 w-4 shrink-0" /> {actionSuccess}
              </div>
            )}
            {actionError && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {actionError}
              </p>
            )}

            {latestScreenshot && (
              <div className="overflow-hidden rounded-md border border-amber-200 bg-card dark:border-amber-900">
                <img
                  src={latestScreenshot.url}
                  alt={`Latest agent screenshot (step ${latestScreenshot.step + 1})`}
                  className="max-h-72 w-full object-contain"
                />
                <p className="px-2 py-1 text-center font-mono text-xs text-amber-700 dark:text-amber-400">
                  Latest screenshot · step {latestScreenshot.step + 1}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={handleOpenLiveBrowser}
                disabled={handoffExpired}
                className="gap-1.5 bg-amber-600 text-white hover:bg-amber-700"
              >
                <ExternalLink className="h-4 w-4" />
                {handoffExpired ? "Live browser expired" : "Open live browser"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleContinueHuman}
                disabled={actionLoading || handoffExpired}
                className="gap-1.5 border-emerald-300 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                I solved it, continue
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAbortHuman}
                disabled={actionLoading}
                className="gap-1.5"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                Abort and re-run fresh
              </Button>
            </div>

            {run.lastCheckpoint && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Last checkpoint: <span className="font-mono">{run.lastCheckpoint}</span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Run metadata */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="font-mono text-base">{run.id}</CardTitle>
              <CardDescription>Agent run detail</CardDescription>
            </div>
            <Badge variant={STATUS_VARIANT[run.status]}>{STATUS_LABEL[run.status]}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">Customer email</dt>
              <dd className="font-medium">{run.customerEmail}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">ASIN</dt>
              <dd className="font-mono font-medium">{run.asin}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Case ID</dt>
              <dd className="font-mono">{run.caseId ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Cost</dt>
              <dd className="tabular-nums">{run.costUsd != null ? `$${run.costUsd}` : "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Duration</dt>
              <dd className="tabular-nums">{formatDuration(run.duration)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Started</dt>
              <dd className="tabular-nums text-muted-foreground">{formatTs(run.startedAt)}</dd>
            </div>
            {run.completedAt && (
              <div>
                <dt className="text-muted-foreground">Completed</dt>
                <dd className="tabular-nums text-muted-foreground">{formatTs(run.completedAt)}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Error section */}
      {hasError && run.errorCategory !== "manual_override" && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive text-sm">Error Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {run.errorCategory && (
              <div>
                <span className="text-xs font-semibold uppercase text-destructive">Category: </span>
                <span className="font-mono text-sm">{run.errorCategory}</span>
              </div>
            )}
            {run.errorMessage && (
              <pre className="rounded bg-destructive/10 p-3 text-xs text-destructive whitespace-pre-wrap break-words">
                {run.errorMessage}
              </pre>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions section — visible on failed or needs_human runs.
          De-emphasized when the human-handoff card is showing above. */}
      {(run.status === "failed" || run.status === "needs_human") && (
        <Card className={isAwaitingHuman ? "opacity-70" : undefined}>
          <CardHeader>
            <CardTitle className="text-sm">
              {isAwaitingHuman ? "Other actions" : "Actions"}
            </CardTitle>
            <CardDescription>
              {isAwaitingHuman
                ? "Manual override fallback. Use the human-action card above when possible."
                : "Re-run the agent or manually resolve this run."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {actionSuccess && (
              <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                {actionSuccess}
              </div>
            )}
            {actionError && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{actionError}</p>
            )}

            {/* Re-run */}
            <div className="space-y-1">
              <p className="text-sm font-medium">Re-run agent</p>
              <p className="text-xs text-muted-foreground">Resets this run to pending so the agent will pick it up again.</p>
              <Button
                variant="outline"
                size="sm"
                disabled={actionLoading}
                onClick={handleRerun}
                className="mt-2 gap-1.5"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                Re-run
              </Button>
            </div>

            <div className="border-t" />

            {/* Manual override */}
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">Manual override</p>
                <p className="text-xs text-muted-foreground">Manually mark this review action as submitted or rejected.</p>
              </div>
              <div className="space-y-1.5 max-w-xs">
                <Label htmlFor="override-case-id" className="text-xs">Case ID (optional)</Label>
                <Input
                  id="override-case-id"
                  placeholder="e.g. 123-456-789"
                  value={overrideCaseId}
                  onChange={(e) => setOverrideCaseId(e.target.value)}
                  className="h-8 text-sm font-mono"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={actionLoading}
                  onClick={() => handleManualOverride("submitted")}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Mark as submitted
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={actionLoading}
                  onClick={() => handleManualOverride("rejected")}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Mark as rejected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Takedown request */}
      {run.takedownRequest && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Takedown Request</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap text-sm text-muted-foreground break-words">
              {run.takedownRequest}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Screenshot carousel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Screenshots</CardTitle>
          <CardDescription>
            {loadingScreenshots
              ? "Loading screenshots…"
              : screenshots.length > 0
              ? `${screenshots.length} screenshot${screenshots.length !== 1 ? "s" : ""} captured`
              : "No screenshots captured"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingScreenshots ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : screenshots.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No screenshots captured</p>
          ) : (
            <div className="space-y-4">
              {/* Step indicator + nav */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentStep <= 0}
                  onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
                >
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <span className="tabular-nums text-sm text-muted-foreground">
                  Step {currentStep + 1} of {screenshots.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentStep >= screenshots.length - 1}
                  onClick={() => setCurrentStep((s) => Math.min(screenshots.length - 1, s + 1))}
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              {/* Image */}
              <div className="flex justify-center rounded border bg-muted/20 p-2">
                <img
                  key={screenshots[currentStep].url}
                  src={screenshots[currentStep].url}
                  alt={`Step ${currentStep + 1}`}
                  className="max-w-full rounded object-contain"
                  style={{ maxHeight: "70vh" }}
                />
              </div>
              <p className="text-center font-mono text-xs text-muted-foreground">
                {screenshots[currentStep].key}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminAgentRunDetail() {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  return (
    <AdminGate>
      <AdminLayout>
        <AdminAgentRunDetailInner runId={id} />
      </AdminLayout>
    </AdminGate>
  );
}
